import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Button, CircularProgress, Stack, type Theme, useMediaQuery } from '@mui/material';
import { BOTTOM_BAR_HEIGHT } from '/@/components/app-navbar/bottombar';
import { _ } from '/@/locale';
import type { RecommendationActionsProps } from '/@/types/recommendation-create';
import useWindowSize from '/@/utils/hooks/window-size';

function RecommendationActions({ steps, activeStep, setActiveStep, isLoading }: RecommendationActionsProps) {
    // Below the app shell's `sm` breakpoint, a fixed app-level BottomBar (zIndex 10000) also
    // sits at bottom:0 — offset above it so this bar isn't painted underneath and hidden.
    const isBelowAppBottomBar = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    // Same threshold the wizard shell uses to switch layouts, so the two agree on "mobile".
    const isMobile = useWindowSize().width <= 900;

    return (
        <Stack
            direction='row'
            p={3}
            justifyContent='space-between'
            borderTop={'1px solid lightgray'}
            sx={{
                // The shell anchors itself to `100dvh`, but the app stacks a navbar above it, so
                // the page itself scrolls a little and an absolutely-positioned bar travels up
                // with it. Pinning to the viewport keeps the actions reachable at every scroll
                // position. Desktop stays absolute: there the bar spans the wizard, not the
                // window, so it must not run under the left nav rail.
                position: isMobile ? 'fixed' : 'absolute',
                ...(isMobile ? { left: 0, zIndex: (theme) => theme.zIndex.appBar } : {}),
                width: '100%',
                bottom: isBelowAppBottomBar ? BOTTOM_BAR_HEIGHT : 0,
                background: 'white',
            }}
        >
            <Button
                variant='outlined'
                color='secondary'
                onClick={
                    steps[activeStep]?.onClickBack
                        ? steps[activeStep]?.onClickBack
                        : () => setActiveStep(activeStep - 1)
                }
                disabled={isLoading || steps[activeStep]?.onClickBackDisabled?.()}
            >
                <Stack direction='row' spacing={1} alignItems='center'>
                    <ArrowBackIosIcon />
                    {steps[activeStep]?.onClickBackText ? steps[activeStep]?.onClickBackText : _('Atrás')}
                </Stack>
            </Button>
            <Button
                variant='contained'
                color='secondary'
                onClick={
                    steps[activeStep]?.onClickNext
                        ? steps[activeStep]?.onClickNext
                        : () => setActiveStep(activeStep + 1)
                }
                disabled={isLoading || steps[activeStep]?.onClickNextDisabled?.()}
            >
                <Stack direction='row' spacing={1} alignItems='center' data-testid='recommendation-actions-next-button'>
                    {isLoading
                        ? _('Cargando')
                        : steps[activeStep]?.onClickNextText
                          ? steps[activeStep]?.onClickNextText
                          : _('Siguiente')}
                    {isLoading ? <CircularProgress size={16} color='inherit' /> : <ArrowForwardIosIcon />}
                </Stack>
            </Button>
        </Stack>
    );
}

export default RecommendationActions;
